import { SetFormValues } from '@type/form-types';
import { MirrorData, SetMirrorData } from '@type/job-add';
import {
  useCallback, useRef, useState, useEffect,
} from 'react';
import {
  getSelectedActivation,
  initialActivationIndex,
} from '@components/templates/job-add/edit/functions/activationFunctions';
import { useSwrActivationServices } from '@hooks/useSwrApi/endpoints';

/** Props */
interface UseJobActivationServices {
  locale: Locale;
  setFormValues: SetFormValues;
  jobSlug?: string;
  inActivePeriod?: boolean,
  apiJobActivationServices?: ApiSchema<'ActivationServices'>;
  reqMirrorData?: Pick<
  MirrorData,
  'title' | 'vacancies' | 'addresses' | 'salaryMin' | 'salaryMax'
  | 'salaryConfidential' | 'workPlacement' | 'domain' | 'ownApplyUrl' | 'repostingNumber'
  >;
  setMirrorData?: SetMirrorData;
}

/**
 * @description Hook for the job activation services.
 * - `locale`: The current app locale.
 * - `jobSlug`: The job slug.
 * - `inActivePeriod`: If the job is in an active period.
 * - `apiJobActivationServices`: The server side activation services data.
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData`: The set mirror data function.
 *
 * @returns The job activation services.
 */
export const useJobActivationServices = (props: UseJobActivationServices) => {
  // Destructure props
  const {
    locale,
    setFormValues,
    jobSlug,
    inActivePeriod,
    apiJobActivationServices,
    reqMirrorData,
    setMirrorData,
  } = props;

  // Mark user has interacted with the activation services
  const hasInteracted = useRef<boolean>(false);

  // Track the last user-selected bundle identifier to avoid race conditions with form state
  const lastSelectedIdentifier = useRef<string | undefined>(undefined);

  // Merge activation services (bundles + subscription)
  const [jobActivationItems, setJobActivationItems] = useState<ApiSchema<'ActivationBundle'>[]>(apiJobActivationServices?.bundles || []);

  // Selected activation index (item in dropdown).
  const [selectedIndex, setSelectedIndex] = useState<number>(
    initialActivationIndex(jobActivationItems, !!inActivePeriod),
  );

  // The currently selected activation package (for displaying the benefits list)
  const [selectedActivation, setSelectedActivation] = useState<ApiSchema<'ActivationBundle'> | undefined>(
    getSelectedActivation(jobActivationItems, !!inActivePeriod),
  );

  // Helper functions
  const benefitIsBold = useCallback(
    (index: number, type: string | undefined) => index === 0
      && (type === 'plus' || type === 'pro' || type === 'hire'),
    [],
  );

  // SWR, get activation services
  // *************************
  const {
    data: activationServicesData,
    isValidating,
  } = useSwrActivationServices({
    locale,
    jobSlug,
    serverData: apiJobActivationServices,
    shouldFetch: !!reqMirrorData?.title && !!reqMirrorData?.vacancies,
    // changes in payload will auto-trigger a fetch
    // don't filter by pricingType if in the edit active job mode
    payload: {
      remote: Boolean(reqMirrorData?.workPlacement === 'REMOTE'),
      vacancies: reqMirrorData?.vacancies,
      ...(reqMirrorData?.domain && { escoId: reqMirrorData.domain }),
      ...(reqMirrorData?.repostingNumber && { repostingNumber: reqMirrorData.repostingNumber }),
      ...(reqMirrorData?.salaryConfidential && { salaryConfidential: reqMirrorData.salaryConfidential }),
      ...(reqMirrorData?.salaryMin && { salary: reqMirrorData.salaryMin }),
      ...(reqMirrorData?.addresses && { locations: reqMirrorData.addresses as never }),
      ...(reqMirrorData?.ownApplyUrl && { ownApplyUrl: reqMirrorData.ownApplyUrl }),
    },
    onDataChanged: (currentData) => {
      const mergedItems = currentData?.bundles || [];
      setJobActivationItems(mergedItems);

      // 1. Check if the user has already made a manual choice
      // Use the local ref identifier as the primary key if we've interacted to avoid race conditions with form state
      const targetIdentifier = lastSelectedIdentifier.current;

      if (hasInteracted.current && targetIdentifier) {
        const userSelectedBundle = mergedItems.find((b) => b.identifier === targetIdentifier);

        // If the bundle user selected is still in the list and available, LOCK it.
        if (userSelectedBundle && userSelectedBundle.available) {
          const bundleIndex = mergedItems.findIndex((b) => b.identifier === targetIdentifier);
          setSelectedIndex(bundleIndex);
          setSelectedActivation(userSelectedBundle);

          // Force sync with Form and Mirror Data immediately
          setFormValues({ packageId: userSelectedBundle.packageId });
          if (setMirrorData) setMirrorData({ bundle: userSelectedBundle.identifier }, false, true);

          return; // EXIT EARLY - COMPLETELY IGNORE SERVER RECOMMENDATIONS
        }
      }

      // 2. Fallback logic: Only runs on initial load or if user choice is no longer available/valid
      const selectedItem = getSelectedActivation(mergedItems, !!inActivePeriod);
      const activationIndex = initialActivationIndex(mergedItems, !!inActivePeriod);

      setSelectedIndex(activationIndex);
      setSelectedActivation(selectedItem);
    },
  });

  // SYNC FORM VALUES. Update when selectedActivation changes.
  // Use a useEffect to ensure form values update happens after render cycle, avoiding race conditions
  // *************************
  useEffect(() => {
    // Only sync if we have a selected activation and user hasn't interacted or the choice is still the same
    // If the user has interacted, the manual selection is already synced in onDataChanged or ActivationType onChange
    if (selectedActivation && (!hasInteracted.current || lastSelectedIdentifier.current === selectedActivation.identifier)) {
      setFormValues({ packageId: selectedActivation.packageId });
      if (setMirrorData) setMirrorData({ bundle: selectedActivation.identifier }, false, true);
    }
  }, [selectedActivation, setFormValues, setMirrorData]);

  // SET MIRROR DATA. Update when jobActivationItems change.
  // *************************
  useEffect(() => {
    if (setMirrorData && jobActivationItems.length > 0) {
      setMirrorData({ currentActivationsGroup: jobActivationItems }, false, true);
    }
  }, [jobActivationItems, setMirrorData]);

  // Return the activation services
  // *************************
  return {
    hasInteracted,
    lastSelectedIdentifier,
    jobActivationItems,
    setJobActivationItems,
    selectedIndex,
    setSelectedIndex,
    selectedActivation,
    setSelectedActivation,
    benefitIsBold,
    activationServicesData,
    isValidating,
  };
};
